<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount } from 'vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { useScheduleDialog } from '@/composition/catalog/use-schedule-dialog';
  import V2CatalogBranchesField from '../shared/V2CatalogBranchesField.vue';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';
  import V2CatalogScheduleSummary from '../shared/V2CatalogScheduleSummary.vue';

  /**
   * «Disponibilidad» (plan §5.12): «Disponible | Oculto», «Sucursales»
   * (multi-branch) and «Horarios». Saved with the product's «Guardar».
   */
  const editor = useProductEditorContext();
  const { data, access } = useCatalogPage();
  const { openSchedule } = useScheduleDialog();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  const form = computed(() => editor.form.value);
  const branches = computed(() => data.activeStoreBranches.value);
  const multiBranch = computed(() => branches.value.length > 1);

  /**
   * «Oculto» hides it in every branch: only roles that manage every branch
   * may change it (the API rule, §5.17), except on a new product.
   */
  const visibleEditable = computed(
    () =>
      editor.canEditAvailability.value &&
      (access.value.canHideEverywhere || editor.itemId === null)
  );
  const visibleLocked = computed(
    () => editor.canEditAvailability.value && !visibleEditable.value
  );

  const states = computed(() => [
    { id: 'on', label: t('menuPage.editor.available') },
    { id: 'off', label: t('menuPage.editor.hidden') },
  ]);

  const hiddenHelp = computed(() =>
    t(
      data.restaurant.value?.settings?.showDisabledItems
        ? 'menuPage.availability.hideBodyUnavailable'
        : 'menuPage.availability.hideBody'
    )
  );

  async function editSchedule() {
    const current = form.value;
    if (!current) return;
    const result = await openSchedule({
      name: current.name.trim() || t('menuPage.editor.newProduct'),
      hourRanges: current.hourRanges,
      entity: 'item',
    });
    // The editor may have closed meanwhile.
    if (result && !unmounted) editor.update('hourRanges', result.hourRanges);
  }
</script>

<template>
  <V2CatalogEditorSection
    v-if="form"
    class="v2-catalog-product-availability"
    :title="t('menuPage.editor.availability')"
  >
    <PosUiSegmented
      :value="form.visible ? 'on' : 'off'"
      :items="states"
      :disabled="!visibleEditable"
      block
      @input="editor.update('visible', $event === 'on')"
    />
    <p v-if="visibleLocked" class="availability-help">
      {{ t('menuPage.editor.needsAllBranches') }}
    </p>
    <p v-else-if="!form.visible" class="availability-help">{{ hiddenHelp }}</p>

    <V2CatalogBranchesField
      v-if="multiBranch"
      class="availability-branches"
      :value="form.hiddenBranchIds"
      :branches="branches"
      :only-branch-id="access.canHideEverywhere ? null : data.branchId.value"
      :disabled="!editor.canEditAvailability.value"
      @input="editor.update('hiddenBranchIds', $event)"
    />

    <V2CatalogScheduleSummary
      class="availability-schedule"
      :value="form.hourRanges"
      :can-edit="access.canSchedule"
      :upsell="access.upsellSchedule"
      :string-id="data.restaurant.value ? data.restaurant.value.stringId : null"
      @edit="editSchedule"
    />
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-product-availability {
    .availability-help {
      @apply tw-mb-0 tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }

    .availability-branches,
    .availability-schedule {
      @apply tw-mt-2 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-1;
    }
  }
</style>
